import { useCallback, useEffect, useMemo, useState } from 'react';

export interface ListStateInit<F> {
  sort: string;
  direction?: 'asc' | 'desc';
  pageSize?: number;
  filters?: F;
}

interface Persisted {
  sort: string;
  direction: 'asc' | 'desc';
  pageSize: number;
}

function readPrefs(key: string): Partial<Persisted> | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<Persisted>) : null;
  } catch {
    return null;
  }
}

/**
 * Local paging/sorting/filtering state producing a `query` object for the API
 * hooks. With `persistKey`, view preferences (sort, direction, page size) are
 * remembered in localStorage. Filters are intentionally *not* auto-persisted so
 * URL deep-links keep working; use Saved Views to persist a full filter set.
 */
export function useListState<F>(init: ListStateInit<F>, opts: { persistKey?: string } = {}) {
  const prefKey = opts.persistKey ? `oip.list.${opts.persistKey}` : null;
  const prefs = useMemo(() => (prefKey ? readPrefs(prefKey) : null), [prefKey]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(prefs?.pageSize ?? init.pageSize ?? 20);
  const [sort, setSort] = useState(prefs?.sort ?? init.sort);
  const [direction, setDirection] = useState<'asc' | 'desc'>(
    prefs?.direction ?? init.direction ?? 'desc',
  );
  const [filters, setFiltersRaw] = useState<F>((init.filters ?? {}) as F);

  useEffect(() => {
    if (prefKey) {
      try {
        localStorage.setItem(prefKey, JSON.stringify({ sort, direction, pageSize }));
      } catch {
        /* ignore */
      }
    }
  }, [prefKey, sort, direction, pageSize]);

  const onSortChange = useCallback((key: string) => {
    setSort((prev) => {
      if (prev === key) {
        setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setDirection('asc');
      return key;
    });
    setPage(1);
  }, []);

  const setSortState = useCallback((nextSort: string, nextDirection: 'asc' | 'desc') => {
    setSort(nextSort);
    setDirection(nextDirection);
    setPage(1);
  }, []);

  const setFilter = useCallback((patch: Partial<F>) => {
    setFiltersRaw((f) => ({ ...f, ...patch }));
    setPage(1);
  }, []);

  const setFilters = useCallback((next: F) => {
    setFiltersRaw(next);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersRaw({} as F);
    setPage(1);
  }, []);

  const setPageSize = useCallback((n: number) => {
    setPageSizeRaw(n);
    setPage(1);
  }, []);

  const query = useMemo(
    () => ({ page, pageSize, sort, direction, ...filters }),
    [page, pageSize, sort, direction, filters],
  );

  return {
    page,
    pageSize,
    sort,
    direction,
    filters,
    setPage,
    setPageSize,
    onSortChange,
    setSortState,
    setFilter,
    setFilters,
    clearFilters,
    query,
  };
}
